<template>
  <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
    <span class="fs-2 fw-bold">{{ currentPlan.name }}</span>
    <span v-if="currentPlan.label" class="badge badge-light-success fs-8">{{ currentPlan.label }}</span>
  </div>
  <div class="mb-6">
    <span class="fs-1 fw-bold text-primary">
      {{ currentPlan.current_price.amount === 0 ? 'Gratis' : currentPlan.current_price.amount }}
      <span v-if="currentPlan.current_price.amount !== 0" class="fs-3">€</span>
    </span>
    <span v-if="currentPlan.current_price.amount !== 0" class="fs-7 text-muted">/ Anual</span>
  </div>

  <div v-if="showItems" class="d-flex flex-column gap-5">
    <div v-if="includedItems.length || includedMoreItems.length" class="d-flex flex-column gap-3">
      <div class="fs-7 fw-bold text-uppercase text-gray-600">Incluye</div>
      <template v-for="item in includedItems" :key="item.hash">
        <div class="d-flex align-items-center">
          <i class="bi bi-check-circle-fill text-success fs-4 me-2"></i>
          <span class="fs-6 text-gray-800">{{ item.name }}</span>
          <PlanItemInfoIcon v-if="item.special_text" :text="item.special_text" class="ms-2" />
        </div>
      </template>
      <template v-if="showMoreIncluded">
        <template v-for="item in includedMoreItems" :key="item.hash">
          <div class="d-flex align-items-center">
            <i class="bi bi-check-circle-fill text-success fs-4 me-2"></i>
            <span class="fs-6 text-gray-800">{{ item.name }}</span>
            <PlanItemInfoIcon v-if="item.special_text" :text="item.special_text" class="ms-2" />
          </div>
        </template>
      </template>
      <a
          v-if="includedMoreItems.length"
          href="#"
          class="link-primary fs-6 fw-semibold"
          @click.prevent="showMoreIncluded = !showMoreIncluded"
      >
        {{ showMoreIncluded ? 'Mostrar menos' : 'Mostrar más' }}
      </a>
    </div>

    <div v-if="excludedItems.length || excludedMoreItems.length" class="d-flex flex-column gap-3">
      <div class="fs-7 fw-bold text-uppercase text-gray-600">No incluye</div>
      <template v-for="item in excludedItems" :key="item.hash">
        <div class="d-flex align-items-center">
          <i class="bi bi-x-circle text-muted fs-4 me-2"></i>
          <span class="fs-6 text-muted">{{ item.name }}</span>
          <PlanItemInfoIcon v-if="item.special_text" :text="item.special_text" class="ms-2" />
        </div>
      </template>
      <template v-if="showMoreExcluded">
        <template v-for="item in excludedMoreItems" :key="item.hash">
          <div class="d-flex align-items-center">
            <i class="bi bi-x-circle text-muted fs-4 me-2"></i>
            <span class="fs-6 text-muted">{{ item.name }}</span>
            <PlanItemInfoIcon v-if="item.special_text" :text="item.special_text" class="ms-2" />
          </div>
        </template>
      </template>
      <a
          v-if="excludedMoreItems.length"
          href="#"
          class="link-primary fs-6 fw-semibold"
          @click.prevent="showMoreExcluded = !showMoreExcluded"
      >
        {{ showMoreExcluded ? 'Mostrar menos' : 'Mostrar más' }}
      </a>
    </div>
  </div>

  <div v-if="currentPlan.recommendations?.length" class="d-flex flex-column gap-2 mt-6">
    <div
        v-for="recommendation in currentPlan.recommendations"
        :key="recommendation.hash"
        class="p-3 rounded bg-light-primary text-gray-800 fs-6"
    >
      <span class="fw-semibold me-1"><b>Elige este plan:</b></span>
      <span class="plan-recommendation-text" v-html="recommendation.text"></span>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, ref} from 'vue';
import PlanItemInfoIcon from '~/components/theme/PlanItemInfoIcon.vue';

export default defineComponent({
  name: 'PlanSummaryDetails',
  components: {
    PlanItemInfoIcon,
  },
  props: {
    currentPlan: {
      type: Object,
      required: true,
    },
    showItems: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const items = computed(() => props.currentPlan.items ?? []);

    const includedItems = computed(() => items.value.filter((item: any) => item.is_included && !item.is_more));
    const includedMoreItems = computed(() => items.value.filter((item: any) => item.is_included && item.is_more));
    const excludedItems = computed(() => items.value.filter((item: any) => !item.is_included && !item.is_more));
    const excludedMoreItems = computed(() => items.value.filter((item: any) => !item.is_included && item.is_more));

    const showMoreIncluded = ref(false);
    const showMoreExcluded = ref(false);

    return {
      includedItems,
      includedMoreItems,
      excludedItems,
      excludedMoreItems,
      showMoreIncluded,
      showMoreExcluded,
    };
  },
});
</script>

<style scoped>
/* El texto de la recomendación viene con HTML desde el admin (párrafos, negritas, enlaces,
   listas...); reseteamos los márgenes por defecto para que no rompa el layout de la tarjeta. */
.plan-recommendation-text :deep(p),
.plan-recommendation-text :deep(ul),
.plan-recommendation-text :deep(ol) {
  margin: 0;
}

.plan-recommendation-text :deep(p + p) {
  margin-top: 0.5rem;
}

.plan-recommendation-text :deep(a) {
  color: inherit;
  text-decoration: underline;
}
</style>
